import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  BaseOneResponseInterface,
  BulkResponseDataInterface,
} from '../../../../shared/model/interface/crud-response-interface.model';
import * as oeeAppReducer from '../../../oee.reducer';
import * as AppActions from '../../../app/actions';
import { ErrorMessageService } from '../../../../shared/service/error-message.service';
import { IStationCombination } from '../../../../view/settings/api-automation-settings/station-combinations/station-combinations.model';
import { StationCombinationService } from './station-combinations.service';
import {
  ActionTypes,
  StationCombinationsLoading,
  StationCombinationsLoaded,
  FetchError,
  GetStationCombination,
  GetStationCombinationCompleted,
  EditStationCombination,
  EditStationCombinationCompleted,
  AddStationCombination,
  AddStationCombinationCompleted,
  DeleteStationCombinations,
  DeleteStationCombinationsCompleted,
  ChangeActivitiesDataLoading,
  ChangeActivitiesDataCompleted,
  HeartbeatSettingsDataLoading,
  HeartbeatSettingsDataCompleted,
} from './station-combinations.actions';
import { ApiAutomationLookupService } from '../shared/api-automation-lookup.service';
import { EApiAutomationResource } from '../shared/api-automation.model';

@Injectable()
export class StationCombinationEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly service: StationCombinationService,
    private readonly store: Store<oeeAppReducer.OeeAppState>,
    private readonly errorMessageService: ErrorMessageService,
    private readonly lookupService: ApiAutomationLookupService,
  ) {}

  getDataForDatatable = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.STATION_COMBINATIONS_DATA_LOADING),
      switchMap((payload: StationCombinationsLoading) => {
        const andConditions = [];
        const searchText: string = payload.params.searchText?.trim() ?? '';
        let httpParams: HttpParams = new HttpParams()
          .append('sort', 'priority,ASC')
          .append('limit', String(payload.params.perPage || 1000))
          .append('page', String(payload.params.page));

        if (searchText) {
          andConditions.push({
            $or: [{ description: { $cont: searchText } }],
          });
        }

        if (andConditions.length > 0) {
          httpParams = httpParams.set('s', JSON.stringify({ $and: andConditions }));
        }

        if (payload.params.orderBy && payload.params.orderDesc !== undefined) {
          httpParams = httpParams.set('sort', `${payload.params.orderBy},${payload.params.orderDesc}`);
        }

        return this.service.getData(httpParams).pipe(
          switchMap((response) => of(new StationCombinationsLoaded(response))),
          catchError((error) => of(new FetchError(error))),
        );
      }),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.GET_STATION_COMBINATION),
      switchMap((objectData: GetStationCombination) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.getOne(objectData.recordId).pipe(
          switchMap((response: BaseOneResponseInterface<IStationCombination>) =>
            of(new GetStationCombinationCompleted(response), new AppActions.HideLoader()),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  addOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.ADD_STATION_COMBINATION),
      switchMap((objectData: AddStationCombination) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.addOne(objectData.record).pipe(
          switchMap((response: BaseOneResponseInterface<IStationCombination>) =>
            of(
              new AddStationCombinationCompleted(response),
              ...(objectData.hideLoaderAfterSuccess ? [new AppActions.HideLoader()] : []),
            ),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  editOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.EDIT_STATION_COMBINATION),
      switchMap((objectData: EditStationCombination) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.editOne(objectData.recordId, objectData.record).pipe(
          switchMap((response: BaseOneResponseInterface<IStationCombination>) =>
            of(
              new EditStationCombinationCompleted(response),
              ...(objectData.hideLoaderAfterSuccess ? [new AppActions.HideLoader()] : []),
            ),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  deleteOnes = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.DELETE_STATION_COMBINATIONS),
      switchMap((objectData: DeleteStationCombinations) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.deleteOnes(objectData.payload).pipe(
          switchMap((response: BulkResponseDataInterface) => {
            this.errorMessageService.getTranslatedErrorMessage(response.data);

            return of(
              new DeleteStationCombinationsCompleted(objectData.payload.length > 1, response),
              new AppActions.HideLoader(),
            );
          }),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  getChangeActivitiesData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.CHANGE_ACTIVITIES_DATA_LOADING),
      switchMap((objectData: ChangeActivitiesDataLoading) =>
        this.lookupService.getOptions(EApiAutomationResource.changeActivity).pipe(
          switchMap((response) => of(new ChangeActivitiesDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getHeartbeatSettingsData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.HEARTBEAT_SETTINGS_DATA_LOADING),
      switchMap((objectData: HeartbeatSettingsDataLoading) =>
        this.lookupService.getOptions(EApiAutomationResource.heartbeatSettings).pipe(
          switchMap((response) => of(new HeartbeatSettingsDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );
}
